import React, { useState } from 'react';
import { X, Plus, Play, Pause } from 'lucide-react';

const StoryPreview = ({ 
  campaignPreview, 
  user, 
  hasCatalog, 
  mediaUrl, 
  hasMedia,
  uploadingMedia,
  loadingImage,
  deletingMedia,
  imageOrientation,
  mediaInputRef, 
  handleImageSelection, 
  removeCurrentImage,
  mediaType
}) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  const handleVideoPause = () => {
    setIsVideoPlaying(false);
  };

  const handleVideoEnded = () => {
    setIsVideoPlaying(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg w-full max-w-xs mx-auto shadow-xl hover:shadow-2xl transition-shadow duration-300">
      <div className="relative w-full aspect-[9/16]">
        {/* Story frame - using full container */}
        <div className="absolute inset-0 bg-white rounded-lg overflow-hidden">
          {/* Content area - clickable for image upload - FULL CONTAINER */}
          <div 
            className={`absolute inset-0 ${!hasMedia ? 'cursor-pointer' : ''}`}
            onClick={!hasMedia ? handleImageSelection : undefined}
          >
            {hasMedia || uploadingMedia ? (
              <div className={`relative w-full h-full ${imageOrientation !== 'portrait' ? 'py-16' : ''}`}>
                {uploadingMedia || loadingImage || deletingMedia ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <div className="flex flex-col items-center space-y-2">
                      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-xs text-gray-500">
                        {deletingMedia ? 'Borrando...' : loadingImage ? 'Cambiando archivo...' : uploadingMedia ? 'Subiendo archivo...' : 'Cargando archivo...'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {mediaType?.includes('video') ? (
                      <video 
                        src={mediaUrl} 
                        className="w-full h-full object-cover"
                        controls
                        muted
                        onPlay={handleVideoPlay}
                        onPause={handleVideoPause}
                        onEnded={handleVideoEnded}
                      />
                    ) : (
                      <img 
                        src={mediaUrl} 
                        alt={mediaType}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {mediaType?.includes('video') && !isVideoPlaying && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black bg-opacity-30 rounded-full p-2">
                          <Play className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                    {mediaType?.includes('video') && isVideoPlaying && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black bg-opacity-30 rounded-full p-2">
                          <Pause className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                  </>
                )}
                
                {/* Edit controls - only show when in edit mode and no catalog */}
                <div className="absolute top-2 right-2 flex gap-1 z-20">
                  {!hasCatalog && (
                    <button
                      onClick={() => mediaInputRef.current?.click()}
                      disabled={uploadingMedia || loadingImage || deletingMedia}
                      className={`w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors shadow-lg ${
                        (uploadingMedia || loadingImage || deletingMedia) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Agregar más imágenes"
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                  {!hasCatalog && (
                    <button
                      onClick={removeCurrentImage}
                      disabled={uploadingMedia || loadingImage || deletingMedia}
                      className={`w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg ${
                        (uploadingMedia || loadingImage || deletingMedia) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title="Eliminar imagen"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className={`w-full h-full bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center ${!hasCatalog ? 'hover:bg-gray-50' : ''}`}>
                <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div className="text-center">
                  <div className={`${hasCatalog ? 'text-gray-500' : 'bg-blue-600 text-white rounded-lg w-4/5 mx-auto py-2'}`}>
                    <span className={`text-xs px-4 py-1.5 font-bold style={{ lineHeight: '2.5' }}`}>
                      {hasCatalog ? 'Usando imágenes del catálogo' : '¡Subí acá tus anuncios! Las dimensiones recomendadas son Cuadradas (1080x1080) y Verticales (1080x1920)'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Progress bar - OVERLAY */}
          <div className="absolute top-2 left-2 right-2 z-10">
            <div className="w-full bg-gray-300 rounded-full h-1">
              <div className="bg-gray-600 h-1 rounded-full" style={{ width: '30%' }}></div>
            </div>
          </div>

          {/* Header with logo and business name - OVERLAY */}
          <div className="absolute top-4 left-4 z-10 flex items-center">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold">
              {(campaignPreview?.business_name || user?.business_name || 'Tu Negocio')[0] || 'B'}
            </div>
            <div className="ml-2 text-gray-900">
              <div className="text-sm font-medium">{campaignPreview?.business_name || user?.business_name || 'Tu Negocio'}</div>
              <div className="text-xs opacity-75">Publicidad</div>
            </div>
          </div>

          {/* Call to action button - OVERLAY */}
          <div className="absolute bottom-4 left-4 right-4 z-10">
            <button className="w-full bg-white text-gray-900 py-2 px-4 rounded-[20px] text-sm font-medium hover:bg-gray-100 transition-colors border border-gray-200">
              Más información
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryPreview; 